/** Copyright (c) 2026 PhiloFund. All rights reserved. */
/**
 * seed-data.ts — Demo Mode Mock Data
 *
 * All realistic data used by the MSW handlers and the DemoAuthProvider.
 * Shapes match the @philofund/shared types exactly — TypeScript will catch
 * any drift.
 *
 * Financial values are always integers in USD cents. Never use floats.
 *
 * DATES ARE RELATIVE: everything is generated from `NOW` (module load time)
 * via daysAgo(n), so "last 7 days" / "last 30 days" / "this month" range
 * filters always have data no matter when the demo runs. Derived datasets
 * (stats, receipts, statements, rosters) are COMPUTED from the donation rows
 * they summarize, so the numbers are consistent by construction.
 */
import type { User, UserRole } from '@philofund/shared/types/user';
import type { Organization } from '@philofund/shared/types/organization';
import type {
  Donation,
  DonationFrequency,
  DonationStatus,
} from '@philofund/shared/types/donation';
import type { DonationStats } from '@/hooks/useDonations';

// ---------------------------------------------------------------------------
// Relative-date helpers
// ---------------------------------------------------------------------------

/** Anchor timestamp — all seed dates are computed relative to this. */
const NOW = Date.now();

const DAY_MS = 86_400_000;

/**
 * ISO-8601 timestamp `n` days before NOW. Fractional days are allowed
 * (0.25 = 6 hours ago); negative values produce FUTURE dates (upcoming
 * charges, pending next-cycle gifts).
 */
function daysAgo(n: number): string {
  return new Date(NOW - n * DAY_MS).toISOString();
}

/** Calendar year of an ISO timestamp (UTC). */
function yearOf(iso: string): number {
  return new Date(iso).getUTCFullYear();
}

const CURRENT_YEAR = new Date(NOW).getUTCFullYear();

// ---------------------------------------------------------------------------
// Demo users (one per role)
// ---------------------------------------------------------------------------

/**
 * The single demo donor that is automatically "logged in" when
 * VITE_DEMO_MODE=true. The Firebase User shape is partially mimicked;
 * DemoAuthProvider converts this to the Zustand auth store shape.
 */
export const DEMO_USER: User = {
  id: 'demo-user-001',
  email: 'alex.morgan@demo.philofund.org',
  displayName: 'Alex Morgan',
  photoUrl: 'https://api.dicebear.com/9.x/avataaars/svg?seed=AlexMorgan&backgroundColor=b6e3f4',
  role: 'donor',
  emailVerified: true,
  mfaEnabled: false,
  createdAt: daysAgo(660),
  updatedAt: daysAgo(4),
};

/**
 * Demo org-admin user. `id` matches DEMO_ORGANIZATIONS[0].adminUserId
 * ('org-admin-001' → Bright Futures Academy) so the org-admin dashboard
 * resolves to a real tenant in the seed.
 */
export const DEMO_ORG_ADMIN_USER: User = {
  id: 'org-admin-001',
  email: 'jordan.reyes@brightfuturesacademy.org',
  displayName: 'Jordan Reyes',
  photoUrl: 'https://api.dicebear.com/9.x/avataaars/svg?seed=JordanReyes&backgroundColor=c0aede',
  role: 'org_admin',
  emailVerified: true,
  mfaEnabled: true,
  createdAt: daysAgo(1200),
  updatedAt: daysAgo(2),
};

/** Demo platform-admin user (AZ1.AI/PhiloFund staff, meta-tenant level). */
export const DEMO_PLATFORM_ADMIN_USER: User = {
  id: 'platform-admin-001',
  email: 'sam.diaz@platform.philofund.org',
  displayName: 'Sam Diaz',
  photoUrl: 'https://api.dicebear.com/9.x/avataaars/svg?seed=SamDiaz&backgroundColor=ffdfbf',
  role: 'platform_admin',
  emailVerified: true,
  mfaEnabled: true,
  createdAt: daysAgo(770),
  updatedAt: daysAgo(1),
};

/**
 * Role → demo user map. DemoAuthProvider indexes this by the selected
 * `demo_role` (localStorage) to mint the fake user's identity.
 */
export const DEMO_USERS_BY_ROLE: Record<UserRole, User> = {
  donor: DEMO_USER,
  org_admin: DEMO_ORG_ADMIN_USER,
  platform_admin: DEMO_PLATFORM_ADMIN_USER,
};

// ---------------------------------------------------------------------------
// Nonprofit organizations (7 orgs, varied causes)
// ---------------------------------------------------------------------------

export const DEMO_ORGANIZATIONS: Organization[] = [
  // --- Education ---
  {
    id: 'org-001',
    adminUserId: 'org-admin-001',
    name: 'Bright Futures Academy',
    ein: '123456789',
    description:
      'Providing after-school tutoring, STEM enrichment programs, and college prep support for underserved youth in urban communities. Since 2012 we have helped over 4,200 students improve their academic outcomes and pursue higher education.',
    website: 'https://example.org/bright-futures-academy',
    logoUrl: 'https://api.dicebear.com/9.x/initials/svg?seed=BF&backgroundColor=2196f3&fontColor=ffffff',
    address: {
      line1: '450 Education Blvd',
      city: 'Chicago',
      state: 'IL',
      postalCode: '60611',
      country: 'US',
    },
    nteeCodes: ['B'],
    size: 'medium',
    annualRevenueCents: 285000000, // $2.85M
    status: 'active',
    stripeConnectStatus: 'active',
    stripeAccountId: 'acct_demo_001',
    totalDonationsReceivedCents: 94500000, // $945,000
    createdAt: daysAgo(1200),
    updatedAt: daysAgo(2),
  },

  // --- Health ---
  {
    id: 'org-002',
    adminUserId: 'org-admin-002',
    name: 'Community Health Forward',
    ein: '234567890',
    description:
      'Operating free and sliding-scale clinics across rural counties, offering primary care, mental health services, and preventive screenings. Our network served 18,000 patients last year who would otherwise have no access to care.',
    website: 'https://example.org/community-health-forward',
    logoUrl: 'https://api.dicebear.com/9.x/initials/svg?seed=CH&backgroundColor=f44336&fontColor=ffffff',
    address: {
      line1: '88 Wellness Way',
      city: 'Nashville',
      state: 'TN',
      postalCode: '37201',
      country: 'US',
    },
    nteeCodes: ['E'],
    size: 'large',
    annualRevenueCents: 1200000000, // $12M
    status: 'active',
    stripeConnectStatus: 'active',
    stripeAccountId: 'acct_demo_002',
    totalDonationsReceivedCents: 312000000, // $3.12M
    createdAt: daysAgo(1420),
    updatedAt: daysAgo(6),
  },

  // --- Environment ---
  {
    id: 'org-003',
    adminUserId: 'org-admin-003',
    name: 'Rewild the Coast',
    ein: '345678901',
    description:
      'Restoring native coastal ecosystems through habitat rehabilitation, invasive species removal, and community science programs. We partner with 40+ municipalities to protect over 120 miles of shoreline and the marine life that depends on healthy estuaries.',
    website: 'https://example.org/rewild-the-coast',
    logoUrl: 'https://api.dicebear.com/9.x/initials/svg?seed=RC&backgroundColor=4caf50&fontColor=ffffff',
    address: {
      line1: '12 Shoreline Drive',
      line2: 'Suite 300',
      city: 'Portland',
      state: 'OR',
      postalCode: '97201',
      country: 'US',
    },
    nteeCodes: ['C'],
    size: 'medium',
    annualRevenueCents: 520000000, // $5.2M
    status: 'active',
    stripeConnectStatus: 'active',
    stripeAccountId: 'acct_demo_003',
    totalDonationsReceivedCents: 178000000, // $1.78M
    createdAt: daysAgo(1860),
    updatedAt: daysAgo(14),
  },

  // --- Arts & Culture ---
  {
    id: 'org-004',
    adminUserId: 'org-admin-004',
    name: 'Mosaic Arts Collective',
    ein: '456789012',
    description:
      'Making world-class arts experiences accessible to everyone. Our programs include free gallery admissions for Title I school groups, subsidized music lessons, and a community mural project that has transformed 65 neighborhoods across three cities.',
    website: 'https://example.org/mosaic-arts-collective',
    logoUrl: 'https://api.dicebear.com/9.x/initials/svg?seed=MA&backgroundColor=9c27b0&fontColor=ffffff',
    address: {
      line1: '220 Gallery Row',
      city: 'New York',
      state: 'NY',
      postalCode: '10001',
      country: 'US',
    },
    nteeCodes: ['A'],
    size: 'small',
    annualRevenueCents: 95000000, // $950K
    status: 'active',
    stripeConnectStatus: 'active',
    stripeAccountId: 'acct_demo_004',
    totalDonationsReceivedCents: 42000000, // $420,000
    createdAt: daysAgo(1020),
    updatedAt: daysAgo(21),
  },

  // --- Housing & Shelter ---
  {
    id: 'org-005',
    adminUserId: 'org-admin-005',
    name: 'Rooted Home Initiative',
    ein: '567890123',
    description:
      'Fighting housing insecurity through transitional housing, rental assistance, and financial literacy coaching. Last year we housed 620 individuals and families and helped 340 households avoid eviction through emergency rental assistance.',
    website: 'https://example.org/rooted-home-initiative',
    logoUrl: 'https://api.dicebear.com/9.x/initials/svg?seed=RH&backgroundColor=ff9800&fontColor=ffffff',
    address: {
      line1: '75 Harbor Street',
      city: 'Seattle',
      state: 'WA',
      postalCode: '98101',
      country: 'US',
    },
    nteeCodes: ['L'],
    size: 'medium',
    annualRevenueCents: 430000000, // $4.3M
    status: 'active',
    stripeConnectStatus: 'active',
    stripeAccountId: 'acct_demo_005',
    totalDonationsReceivedCents: 156000000, // $1.56M
    createdAt: daysAgo(1550),
    updatedAt: daysAgo(4),
  },

  // --- Food Security ---
  {
    id: 'org-006',
    adminUserId: 'org-admin-006',
    name: 'Tables of Plenty',
    ein: '678901234',
    description:
      'Operating regional food banks, community gardens, and a fresh produce delivery program for seniors and families with young children. We distribute over 2 million meals annually and have zero food deserts in the communities we serve.',
    website: 'https://example.org/tables-of-plenty',
    logoUrl: 'https://api.dicebear.com/9.x/initials/svg?seed=TP&backgroundColor=795548&fontColor=ffffff',
    address: {
      line1: '500 Harvest Lane',
      city: 'Austin',
      state: 'TX',
      postalCode: '78701',
      country: 'US',
    },
    nteeCodes: ['K'],
    size: 'large',
    annualRevenueCents: 890000000, // $8.9M
    status: 'active',
    stripeConnectStatus: 'active',
    stripeAccountId: 'acct_demo_006',
    totalDonationsReceivedCents: 267000000, // $2.67M
    createdAt: daysAgo(2050),
    updatedAt: daysAgo(1),
  },

  // --- Youth Development ---
  {
    id: 'org-007',
    adminUserId: 'org-admin-007',
    name: 'NextGen Leaders',
    ein: '789012345',
    description:
      'Building leadership skills and career readiness for teens from low-income households through mentorship, internships, and entrepreneurship bootcamps. 92% of our graduates are enrolled in college or a career pathway within 12 months.',
    website: 'https://example.org/nextgen-leaders',
    logoUrl: 'https://api.dicebear.com/9.x/initials/svg?seed=NL&backgroundColor=00bcd4&fontColor=ffffff',
    address: {
      line1: '1400 Mission Street',
      city: 'San Francisco',
      state: 'CA',
      postalCode: '94103',
      country: 'US',
    },
    nteeCodes: ['O'],
    size: 'small',
    annualRevenueCents: 72000000, // $720K
    status: 'active',
    stripeConnectStatus: 'active',
    stripeAccountId: 'acct_demo_007',
    totalDonationsReceivedCents: 31500000, // $315,000
    createdAt: daysAgo(520),
    updatedAt: daysAgo(1),
  },
];

// ---------------------------------------------------------------------------
// Demo donor donation history (13 records — ids don-001..don-013 are STABLE,
// referenced by tests, receipts and queues; only their dates are generated)
// ---------------------------------------------------------------------------

export const DEMO_DONATIONS: Donation[] = [
  // Org 001 — Education (recurring monthly, last three cycles)
  {
    id: 'don-001',
    donorUserId: DEMO_USER.id,
    organizationId: 'org-001',
    amountCents: 5000,          // $50.00
    feeCents: 150,              // $1.50 platform fee
    netAmountCents: 4850,
    frequency: 'monthly',
    currency: 'USD',
    status: 'completed',
    stripePaymentIntentId: 'pi_demo_001',
    stripeSubscriptionId: 'sub_demo_001',
    anonymous: false,
    completedAt: daysAgo(3),
    createdAt: daysAgo(3.004),
    updatedAt: daysAgo(3),
  },
  {
    id: 'don-002',
    donorUserId: DEMO_USER.id,
    organizationId: 'org-001',
    amountCents: 5000,
    feeCents: 150,
    netAmountCents: 4850,
    frequency: 'monthly',
    currency: 'USD',
    status: 'completed',
    stripePaymentIntentId: 'pi_demo_002',
    stripeSubscriptionId: 'sub_demo_001',
    anonymous: false,
    completedAt: daysAgo(33),
    createdAt: daysAgo(33.004),
    updatedAt: daysAgo(33),
  },
  {
    id: 'don-003',
    donorUserId: DEMO_USER.id,
    organizationId: 'org-001',
    amountCents: 5000,
    feeCents: 150,
    netAmountCents: 4850,
    frequency: 'monthly',
    currency: 'USD',
    status: 'completed',
    stripePaymentIntentId: 'pi_demo_003',
    stripeSubscriptionId: 'sub_demo_001',
    anonymous: false,
    completedAt: daysAgo(63),
    createdAt: daysAgo(63.004),
    updatedAt: daysAgo(63),
  },

  // Org 003 — Environment (one-time, larger gift)
  {
    id: 'don-004',
    donorUserId: DEMO_USER.id,
    organizationId: 'org-003',
    amountCents: 25000,         // $250.00
    feeCents: 750,
    netAmountCents: 24250,
    frequency: 'one_time',
    currency: 'USD',
    status: 'completed',
    stripePaymentIntentId: 'pi_demo_004',
    message: 'Love what you are doing for our coastlines. Keep it up!',
    anonymous: false,
    completedAt: daysAgo(48),
    createdAt: daysAgo(48.004),
    updatedAt: daysAgo(48),
  },

  // Org 006 — Food (recurring monthly, last two cycles)
  {
    id: 'don-005',
    donorUserId: DEMO_USER.id,
    organizationId: 'org-006',
    amountCents: 2500,          // $25.00
    feeCents: 75,
    netAmountCents: 2425,
    frequency: 'monthly',
    currency: 'USD',
    status: 'completed',
    stripePaymentIntentId: 'pi_demo_005',
    stripeSubscriptionId: 'sub_demo_002',
    anonymous: false,
    completedAt: daysAgo(6),
    createdAt: daysAgo(6.004),
    updatedAt: daysAgo(6),
  },
  {
    id: 'don-006',
    donorUserId: DEMO_USER.id,
    organizationId: 'org-006',
    amountCents: 2500,
    feeCents: 75,
    netAmountCents: 2425,
    frequency: 'monthly',
    currency: 'USD',
    status: 'completed',
    stripePaymentIntentId: 'pi_demo_006',
    stripeSubscriptionId: 'sub_demo_002',
    anonymous: false,
    completedAt: daysAgo(36),
    createdAt: daysAgo(36.004),
    updatedAt: daysAgo(36),
  },

  // Org 002 — Health (one-time, giving day)
  {
    id: 'don-007',
    donorUserId: DEMO_USER.id,
    organizationId: 'org-002',
    amountCents: 10000,         // $100.00
    feeCents: 300,
    netAmountCents: 9700,
    frequency: 'one_time',
    currency: 'USD',
    status: 'completed',
    stripePaymentIntentId: 'pi_demo_007',
    message: 'In honor of my grandmother who benefited from free clinics.',
    anonymous: false,
    completedAt: daysAgo(150),
    createdAt: daysAgo(150.004),
    updatedAt: daysAgo(150),
  },

  // Org 005 — Housing (quarterly, last two cycles)
  {
    id: 'don-008',
    donorUserId: DEMO_USER.id,
    organizationId: 'org-005',
    amountCents: 15000,         // $150.00
    feeCents: 450,
    netAmountCents: 14550,
    frequency: 'quarterly',
    currency: 'USD',
    status: 'completed',
    stripePaymentIntentId: 'pi_demo_008',
    stripeSubscriptionId: 'sub_demo_003',
    anonymous: false,
    completedAt: daysAgo(18),
    createdAt: daysAgo(18.004),
    updatedAt: daysAgo(18),
  },
  {
    id: 'don-009',
    donorUserId: DEMO_USER.id,
    organizationId: 'org-005',
    amountCents: 15000,
    feeCents: 450,
    netAmountCents: 14550,
    frequency: 'quarterly',
    currency: 'USD',
    status: 'completed',
    stripePaymentIntentId: 'pi_demo_009',
    stripeSubscriptionId: 'sub_demo_003',
    anonymous: false,
    completedAt: daysAgo(108),
    createdAt: daysAgo(108.004),
    updatedAt: daysAgo(108),
  },

  // Org 004 — Arts (one-time, holiday gift — always lands in the PREVIOUS
  // calendar year so the finalized year-end statement is never empty)
  {
    id: 'don-010',
    donorUserId: DEMO_USER.id,
    organizationId: 'org-004',
    amountCents: 7500,          // $75.00
    feeCents: 225,
    netAmountCents: 7275,
    frequency: 'one_time',
    currency: 'USD',
    status: 'completed',
    stripePaymentIntentId: 'pi_demo_010',
    anonymous: true,
    completedAt: daysAgo(390),
    createdAt: daysAgo(390.004),
    updatedAt: daysAgo(390),
  },

  // Org 007 — Youth (one-time)
  {
    id: 'don-011',
    donorUserId: DEMO_USER.id,
    organizationId: 'org-007',
    amountCents: 5000,          // $50.00
    feeCents: 150,
    netAmountCents: 4850,
    frequency: 'one_time',
    currency: 'USD',
    status: 'completed',
    stripePaymentIntentId: 'pi_demo_011',
    message: 'Great work mentoring the next generation!',
    anonymous: false,
    completedAt: daysAgo(240),
    createdAt: daysAgo(240.004),
    updatedAt: daysAgo(240),
  },

  // Org 003 — Environment (processing — just submitted)
  {
    id: 'don-012',
    donorUserId: DEMO_USER.id,
    organizationId: 'org-003',
    amountCents: 10000,         // $100.00
    feeCents: 300,
    netAmountCents: 9700,
    frequency: 'one_time',
    currency: 'USD',
    status: 'processing',
    stripePaymentIntentId: 'pi_demo_012',
    anonymous: false,
    createdAt: daysAgo(0.3),
    updatedAt: daysAgo(0.3),
  },

  // Org 001 — Education (upcoming monthly — future date to show active recurring)
  {
    id: 'don-013',
    donorUserId: DEMO_USER.id,
    organizationId: 'org-001',
    amountCents: 5000,
    feeCents: 150,
    netAmountCents: 4850,
    frequency: 'monthly',
    currency: 'USD',
    status: 'pending',
    stripeSubscriptionId: 'sub_demo_001',
    anonymous: false,
    createdAt: daysAgo(-27),
    updatedAt: daysAgo(-27),
  },
];

// ---------------------------------------------------------------------------
// Platform donor directory (10 distinct donor names reused across orgs —
// platform-side only; donor-001 is the demo donor Alex Morgan)
// ---------------------------------------------------------------------------

interface PlatformDonorSeed {
  /** Roster id AND the donorUserId used on generated donations. */
  id: string;
  name: string;
  email: string;
}

/** The 9 non-demo platform donors (donor-002..donor-010). */
const PLATFORM_DONORS: PlatformDonorSeed[] = [
  { id: 'donor-002', name: 'Riley Chen', email: 'riley.chen@example.com' },
  { id: 'donor-003', name: 'Morgan Patel', email: 'morgan.patel@example.com' },
  { id: 'donor-004', name: 'Jamie Okafor', email: 'jamie.okafor@example.com' },
  { id: 'donor-005', name: 'Casey Nguyen', email: 'casey.nguyen@example.com' },
  { id: 'donor-006', name: 'Dana Whitfield', email: 'dana.whitfield@example.com' },
  { id: 'donor-007', name: 'Marcus Webb', email: 'marcus.webb@example.com' },
  { id: 'donor-008', name: 'Sofia Ramirez', email: 'sofia.ramirez@example.com' },
  { id: 'donor-009', name: 'Jordan Ellis', email: 'jordan.ellis@example.com' },
  { id: 'donor-010', name: 'Elena Vasquez', email: 'elena.vasquez@example.com' },
];

// ---------------------------------------------------------------------------
// Per-org generated donations (complete datasets for all 7 orgs)
//
// Deterministic layout per org (i is the 1-based row number):
//   i=1  completed  — dispute/refund ANCHOR row (queues link to don-<org>-1)
//   i=2  refunded   — every org has at least one refund-relevant donation
//   i=3  completed  — within the last 7 days (keeps "this week" filters alive)
//   i=4  pending    — just created, no completedAt
//   i=5  failed     — card declined
//   i=6  completed  — recurring monthly (sub_<org>_gen1)
//   i=8  completed  — recurring quarterly (sub_<org>_gen2)
//   else completed  — one-time, dates spread over the last 400 days
// ---------------------------------------------------------------------------

/** $10 … $2,500 in cents. */
const AMOUNT_PALETTE_CENTS = [
  1000, 1500, 2500, 4000, 5000, 7500, 10000, 15000,
  20000, 25000, 40000, 60000, 75000, 100000, 150000, 250000,
];

/** Tail of one-time gift ages, out to 400 days. */
const DAY_SPREAD = [9, 14, 21, 30, 45, 60, 85, 120, 160, 210, 260, 320, 370, 400];

const DONOR_MESSAGES = [
  'Keep up the amazing work!',
  'Happy to support this cause.',
  'In memory of a dear friend.',
  'Matching my company gift program.',
  'Proud to give locally.',
  'You are making a real difference.',
  'From our family to yours.',
];

/** Generated donation counts per org (donor rows above add to these). */
const ORG_GENERATION_SPECS: Array<{ orgId: string; count: number }> = [
  { orgId: 'org-001', count: 12 },
  { orgId: 'org-002', count: 11 },
  { orgId: 'org-003', count: 12 },
  { orgId: 'org-004', count: 9 },
  { orgId: 'org-005', count: 10 },
  { orgId: 'org-006', count: 14 },
  { orgId: 'org-007', count: 8 },
];

/**
 * Hand-pinned anchor rows so the HITL queues reference REAL donations:
 *   dsp-002 → don-org-004-1 (Riley Chen, $75)
 *   ref-003 → don-org-006-1 (Morgan Patel, $600)
 */
const ANCHOR_OVERRIDES: Record<string, { donorIndex: number; amountCents: number }> = {
  'org-004': { donorIndex: 0, amountCents: 7500 },   // Riley Chen
  'org-006': { donorIndex: 1, amountCents: 60000 },  // Morgan Patel
};

function buildOrgDonations(): Donation[] {
  const rows: Donation[] = [];

  ORG_GENERATION_SPECS.forEach((spec, orgIndex) => {
    for (let i = 1; i <= spec.count; i++) {
      // Deterministic donor rotation — the same 9 platform donors recur
      // across orgs. Anchor rows (i=1) may pin a specific donor.
      let donorIndex = (orgIndex * 3 + i * 2) % PLATFORM_DONORS.length;
      let amountCents =
        AMOUNT_PALETTE_CENTS[(orgIndex * 5 + i * 7) % AMOUNT_PALETTE_CENTS.length];
      const anchor = i === 1 ? ANCHOR_OVERRIDES[spec.orgId] : undefined;
      if (anchor !== undefined) {
        donorIndex = anchor.donorIndex;
        amountCents = anchor.amountCents;
      }

      let status: DonationStatus = 'completed';
      let frequency: DonationFrequency = 'one_time';
      let subscriptionId: string | undefined;
      let day: number;
      switch (i) {
        case 1:
          day = 15 + orgIndex;
          break;
        case 2:
          status = 'refunded';
          day = 22 + orgIndex;
          break;
        case 3:
          day = 1 + (orgIndex % 6); // last 7 days
          break;
        case 4:
          status = 'pending';
          day = 0.2;
          break;
        case 5:
          status = 'failed';
          day = 8 + orgIndex;
          break;
        case 6:
          frequency = 'monthly';
          subscriptionId = `sub_${spec.orgId}_gen1`;
          day = 12 + orgIndex;
          break;
        case 8:
          frequency = 'quarterly';
          subscriptionId = `sub_${spec.orgId}_gen2`;
          day = 40 + orgIndex;
          break;
        default:
          day = DAY_SPREAD[(orgIndex * 3 + i) % DAY_SPREAD.length];
          break;
      }

      const settled = status === 'completed' || status === 'refunded';
      const feeCents = Math.round(amountCents * 0.03);
      const createdAt = daysAgo(day + 0.004);
      const settledAt = daysAgo(day);
      const donor = PLATFORM_DONORS[donorIndex];

      rows.push({
        id: `don-${spec.orgId}-${i}`,
        donorUserId: donor.id,
        organizationId: spec.orgId,
        amountCents,
        feeCents,
        netAmountCents: amountCents - feeCents,
        frequency,
        currency: 'USD',
        status,
        ...(status !== 'pending'
          ? { stripePaymentIntentId: `pi_${spec.orgId}_${i}` }
          : {}),
        ...(subscriptionId !== undefined ? { stripeSubscriptionId: subscriptionId } : {}),
        ...(i === 3 ? { message: DONOR_MESSAGES[(orgIndex + i) % DONOR_MESSAGES.length] } : {}),
        anonymous: (orgIndex + i) % 5 === 0,
        paymentMethod: (['card', 'card', 'card', 'bank', 'apple_pay', 'google_pay'] as const)[(orgIndex + i) % 6],
        ...(settled ? { completedAt: settledAt } : {}),
        createdAt,
        updatedAt: settled ? settledAt : createdAt,
      });
    }
  });

  return rows;
}

/** Platform-side donations across all 7 orgs (8–20 rows per org incl. demo donor's). */
export const DEMO_ORG_DONATIONS: Donation[] = buildOrgDonations();

/** Every donation on the platform (demo donor's + generated per-org sets). */
export const DEMO_ALL_DONATIONS: Donation[] = [...DEMO_DONATIONS, ...DEMO_ORG_DONATIONS];

// ---------------------------------------------------------------------------
// Donation stats (COMPUTED from the demo donor's donations — consistent
// with every list/filter view by construction)
// ---------------------------------------------------------------------------

const donorCompleted = DEMO_DONATIONS.filter(
  (d) => d.status === 'completed' && d.completedAt !== undefined,
);

export const DEMO_DONATION_STATS: DonationStats = {
  totalLifetimeCents: donorCompleted.reduce((sum, d) => sum + d.amountCents, 0),
  ytdCents: donorCompleted
    .filter((d) => yearOf(d.completedAt as string) === CURRENT_YEAR)
    .reduce((sum, d) => sum + d.amountCents, 0),
  organizationCount: new Set(DEMO_DONATIONS.map((d) => d.organizationId)).size,
  activeRecurringCount: new Set(
    DEMO_DONATIONS.filter((d) => d.stripeSubscriptionId !== undefined).map(
      (d) => d.stripeSubscriptionId,
    ),
  ).size,
};

// ---------------------------------------------------------------------------
// Donor receipts (COMPUTED from the donations they reference — ids rcpt-001..
// rcpt-006 are stable; org name / amount / date always match the donation)
// ---------------------------------------------------------------------------

/** IRS Pub-1771 receipt record for the donor receipts library. */
export interface ReceiptRecord {
  id: string;
  donationId: string;
  orgName: string;
  dateISO: string;
  amountCents: number;
  receiptNumber: string;
  pdfUrl: string;
}

function receiptFor(id: string, donationId: string, seq: number): ReceiptRecord {
  const donation = DEMO_DONATIONS.find((d) => d.id === donationId);
  if (donation === undefined) throw new Error(`receiptFor: unknown donation ${donationId}`);
  const org = DEMO_ORGANIZATIONS.find((o) => o.id === donation.organizationId);
  if (org === undefined) throw new Error(`receiptFor: unknown org ${donation.organizationId}`);
  const dateISO = donation.completedAt ?? donation.createdAt;
  return {
    id,
    donationId,
    orgName: org.name,
    dateISO,
    amountCents: donation.amountCents,
    receiptNumber: `PF-${yearOf(dateISO)}-${String(seq).padStart(6, '0')}`,
    pdfUrl: '#',
  };
}

export const DEMO_RECEIPTS: ReceiptRecord[] = [
  receiptFor('rcpt-001', 'don-001', 481),
  receiptFor('rcpt-002', 'don-004', 502),
  receiptFor('rcpt-003', 'don-005', 479),
  receiptFor('rcpt-004', 'don-007', 401),
  receiptFor('rcpt-005', 'don-008', 288),
  receiptFor('rcpt-006', 'don-010', 1194),
];

// ---------------------------------------------------------------------------
// Year-end statements (COMPUTED per calendar year from the demo donor's
// completed donations — current year is a draft, prior years are finalized)
// ---------------------------------------------------------------------------

/** Year-end giving statement (draft in-year, finalized after Jan 15). */
export interface YearEndStatement {
  year: number;
  totalCents: number;
  deductibleCents: number;
  donationCount: number;
  finalized: boolean;
  orgs: Array<{ orgName: string; totalCents: number }>;
}

function buildStatements(): Record<string, YearEndStatement> {
  const byYear = new Map<number, Donation[]>();
  for (const d of donorCompleted) {
    const year = yearOf(d.completedAt as string);
    const bucket = byYear.get(year) ?? [];
    bucket.push(d);
    byYear.set(year, bucket);
  }
  // The current year always has a statement, even if (early January) nothing
  // has settled yet this year.
  if (!byYear.has(CURRENT_YEAR)) byYear.set(CURRENT_YEAR, []);

  const statements: Record<string, YearEndStatement> = {};
  for (const [year, donations] of byYear) {
    const perOrg = new Map<string, number>();
    for (const d of donations) {
      const orgName =
        DEMO_ORGANIZATIONS.find((o) => o.id === d.organizationId)?.name ?? d.organizationId;
      perOrg.set(orgName, (perOrg.get(orgName) ?? 0) + d.amountCents);
    }
    const totalCents = donations.reduce((sum, d) => sum + d.amountCents, 0);
    statements[String(year)] = {
      year,
      totalCents,
      deductibleCents: totalCents,
      donationCount: donations.length,
      finalized: year < CURRENT_YEAR,
      orgs: [...perOrg.entries()]
        .map(([orgName, cents]) => ({ orgName, totalCents: cents }))
        .sort((a, b) => b.totalCents - a.totalCents),
    };
  }
  return statements;
}

export const DEMO_STATEMENTS: Record<string, YearEndStatement> = buildStatements();

// ---------------------------------------------------------------------------
// Platform-admin HITL queues (verification / disputes / refunds)
// Every donationId below points at a REAL row in DEMO_ALL_DONATIONS.
// ---------------------------------------------------------------------------

export interface VerificationQueueItem {
  id: string;
  orgName: string;
  ein: string;
  submittedAt: string;
  contactEmail: string;
  docsUrl: string;
  status: 'pending' | 'approved' | 'rejected';
}

export const DEMO_VERIFICATION_QUEUE: VerificationQueueItem[] = [
  { id: 'vq-001', orgName: 'Tables of Plenty', ein: '67-8901234', submittedAt: daysAgo(2.2), contactEmail: 'priya.nair@tablesofplenty.org', docsUrl: '#', status: 'pending' },
  { id: 'vq-002', orgName: 'Harbor Light Shelter', ein: '81-2345678', submittedAt: daysAgo(3.1), contactEmail: 'ops@harborlightshelter.org', docsUrl: '#', status: 'pending' },
  { id: 'vq-003', orgName: 'Prairie Roots Conservancy', ein: '45-6789012', submittedAt: daysAgo(3.8), contactEmail: 'admin@prairieroots.org', docsUrl: '#', status: 'pending' },
];

export interface DisputeItem {
  id: string;
  donationId: string;
  orgName: string;
  donorName: string;
  amountCents: number;
  reason: string;
  openedAt: string;
  status: 'open' | 'under_review' | 'resolved';
}

export const DEMO_DISPUTES: DisputeItem[] = [
  { id: 'dsp-001', donationId: 'don-007', orgName: 'Community Health Forward', donorName: 'Alex Morgan', amountCents: 10000, reason: 'Donor reports duplicate charge for the same donation.', openedAt: daysAgo(1.4), status: 'open' },
  { id: 'dsp-002', donationId: 'don-org-004-1', orgName: 'Mosaic Arts Collective', donorName: 'Riley Chen', amountCents: 7500, reason: 'Card issuer flagged the transaction as unrecognized.', openedAt: daysAgo(4.5), status: 'under_review' },
];

export interface RefundItem {
  id: string;
  donationId: string;
  orgName: string;
  donorName: string;
  amountCents: number;
  requestedAt: string;
  requiresApproval: boolean;
  status: 'pending' | 'approved' | 'rejected';
}

export const DEMO_REFUNDS: RefundItem[] = [
  { id: 'ref-001', donationId: 'don-010', orgName: 'Mosaic Arts Collective', donorName: 'Alex Morgan', amountCents: 7500, requestedAt: daysAgo(2.6), requiresApproval: true, status: 'pending' },
  { id: 'ref-002', donationId: 'don-002', orgName: 'Bright Futures Academy', donorName: 'Alex Morgan', amountCents: 5000, requestedAt: daysAgo(3.4), requiresApproval: false, status: 'approved' },
  { id: 'ref-003', donationId: 'don-org-006-1', orgName: 'Tables of Plenty', donorName: 'Morgan Patel', amountCents: 60000, requestedAt: daysAgo(4.1), requiresApproval: true, status: 'pending' },
];

// ---------------------------------------------------------------------------
// Donor rosters (COMPUTED from DEMO_ALL_DONATIONS)
// ---------------------------------------------------------------------------

export interface AdminDonorRecord {
  id: string;
  name: string;
  email: string;
  totalDonatedCents: number;
  donationCount: number;
  lastDonationAt: string;
}

/** Directory of every donor identity on the platform (demo donor first). */
const DONOR_DIRECTORY: Array<PlatformDonorSeed & { userId: string }> = [
  { id: 'donor-001', name: DEMO_USER.displayName, email: DEMO_USER.email, userId: DEMO_USER.id },
  ...PLATFORM_DONORS.map((p) => ({ ...p, userId: p.id })),
];

function aggregateDonor(
  seed: (typeof DONOR_DIRECTORY)[number],
  donations: Donation[],
): AdminDonorRecord {
  const mine = donations.filter((d) => d.donorUserId === seed.userId);
  const completed = mine.filter((d) => d.status === 'completed');
  const lastDonationAt = mine.reduce(
    (latest, d) => {
      const ts = d.completedAt ?? d.createdAt;
      return ts > latest ? ts : latest;
    },
    daysAgo(400),
  );
  return {
    id: seed.id,
    name: seed.name,
    email: seed.email,
    totalDonatedCents: completed.reduce((sum, d) => sum + d.amountCents, 0),
    donationCount: completed.length,
    lastDonationAt,
  };
}

/** Platform-wide donor roster for GET /api/admin/donors (10 donors). */
export const DEMO_ADMIN_DONORS: AdminDonorRecord[] = DONOR_DIRECTORY.map((seed) =>
  aggregateDonor(seed, DEMO_ALL_DONATIONS),
);

/** Per-org donor rosters (orgId → donors who gave to that org). */
export const DEMO_ORG_DONOR_ROSTERS: Record<string, AdminDonorRecord[]> =
  Object.fromEntries(
    DEMO_ORGANIZATIONS.map((org) => {
      const orgDonations = DEMO_ALL_DONATIONS.filter((d) => d.organizationId === org.id);
      const donorIds = new Set(orgDonations.map((d) => d.donorUserId));
      const roster = DONOR_DIRECTORY.filter((seed) => donorIds.has(seed.userId))
        .map((seed) => aggregateDonor(seed, orgDonations))
        .sort((a, b) => b.totalDonatedCents - a.totalDonatedCents);
      return [org.id, roster];
    }),
  );

// ---------------------------------------------------------------------------
// Platform-admin dashboard payloads (cross-tenant demo numbers)
// ---------------------------------------------------------------------------

/** Response shape for GET /api/admin/platform-stats. */
export interface PlatformStats {
  /** Lifetime gross donation volume across all tenants, in USD cents. */
  totalDonationsCents: number;
  /** Count of organizations with status 'active'. */
  activeOrgs: number;
  /** Distinct donor accounts across the platform. */
  totalDonors: number;
  /** Lifetime platform fees collected, in USD cents. */
  platformFeeCents: number;
  /** Operational review queues awaiting platform-admin action. */
  queues: {
    einVerification: number;
    disputes: number;
    refunds: number;
  };
}

const lifetimeVolumeCents = DEMO_ORGANIZATIONS.reduce(
  (sum, o) => sum + o.totalDonationsReceivedCents,
  0,
);

/**
 * Platform-wide summary — COMPUTED: volume mirrors the sum of
 * totalDonationsReceivedCents across the 7 seed orgs; fee is the 3% platform
 * fee on that volume; queue badges count the actionable queue rows.
 */
export const DEMO_PLATFORM_STATS: PlatformStats = {
  totalDonationsCents: lifetimeVolumeCents,
  activeOrgs: DEMO_ORGANIZATIONS.filter((o) => o.status === 'active').length,
  totalDonors: DEMO_ADMIN_DONORS.length,
  platformFeeCents: Math.round(lifetimeVolumeCents * 0.03),
  queues: {
    einVerification: DEMO_VERIFICATION_QUEUE.filter((v) => v.status === 'pending').length,
    disputes: DEMO_DISPUTES.filter((d) => d.status !== 'resolved').length,
    refunds: DEMO_REFUNDS.filter((r) => r.status === 'pending').length,
  },
};

/** A single audit/activity item for GET /api/admin/recent-events. */
export interface RecentEvent {
  id: string;
  ts: string; // ISO-8601
  actor: string;
  action: string;
  target: string;
}

/** Recent platform activity feed (newest first, dates relative to NOW). */
export const DEMO_RECENT_EVENTS: RecentEvent[] = [
  {
    id: 'evt-001',
    ts: daysAgo(0.2),
    actor: 'Sam Diaz',
    action: 'approved EIN verification',
    target: 'NextGen Leaders (EIN 78-9012345)',
  },
  {
    id: 'evt-002',
    ts: daysAgo(0.35),
    actor: 'Alex Morgan',
    action: 'made a donation',
    target: 'Rewild the Coast — $100.00',
  },
  {
    id: 'evt-003',
    ts: daysAgo(1.1),
    actor: 'Jordan Reyes',
    action: 'updated organization profile',
    target: 'Bright Futures Academy',
  },
  {
    id: 'evt-004',
    ts: daysAgo(1.4),
    actor: 'Sam Diaz',
    action: 'opened a dispute review',
    target: 'Community Health Forward — $100.00',
  },
  {
    id: 'evt-005',
    ts: daysAgo(2.2),
    actor: 'Priya Nair',
    action: 'submitted EIN verification',
    target: 'Tables of Plenty (EIN 67-8901234)',
  },
  {
    id: 'evt-006',
    ts: daysAgo(2.6),
    actor: 'Sam Diaz',
    action: 'issued a refund',
    target: 'Mosaic Arts Collective — $75.00',
  },
];

// ---------------------------------------------------------------------------
// Donor "Outgoing" tab — upcoming scheduled/recurring gifts
// ---------------------------------------------------------------------------

/** A scheduled next charge on one of the demo donor's active subscriptions. */
export interface UpcomingGift {
  id: string;
  subscriptionId: string;
  organizationId: string;
  orgName: string;
  amountCents: number;
  frequency: DonationFrequency;
  /** ISO-8601 — always in the future relative to NOW. */
  nextChargeAt: string;
  status: 'scheduled' | 'paused';
}

export const DEMO_UPCOMING: UpcomingGift[] = [
  {
    id: 'up-001',
    subscriptionId: 'sub_demo_001',
    organizationId: 'org-001',
    orgName: 'Bright Futures Academy',
    amountCents: 5000,
    frequency: 'monthly',
    nextChargeAt: daysAgo(-27), // last cycle settled 3 days ago
    status: 'scheduled',
  },
  {
    id: 'up-002',
    subscriptionId: 'sub_demo_002',
    organizationId: 'org-006',
    orgName: 'Tables of Plenty',
    amountCents: 2500,
    frequency: 'monthly',
    nextChargeAt: daysAgo(-24), // last cycle settled 6 days ago
    status: 'scheduled',
  },
  {
    id: 'up-003',
    subscriptionId: 'sub_demo_003',
    organizationId: 'org-005',
    orgName: 'Rooted Home Initiative',
    amountCents: 15000,
    frequency: 'quarterly',
    nextChargeAt: daysAgo(-72), // last cycle settled 18 days ago
    status: 'scheduled',
  },
];

// ---------------------------------------------------------------------------
// "News" tab — short update posts from the seed orgs
// ---------------------------------------------------------------------------

/** A short organization update post for GET /api/news. */
export interface OrgNewsPost {
  id: string;
  organizationId: string;
  orgName: string;
  title: string;
  body: string;
  publishedAt: string;
}

export const DEMO_ORG_NEWS: OrgNewsPost[] = [
  {
    id: 'news-001',
    organizationId: 'org-001',
    orgName: 'Bright Futures Academy',
    title: '1,200 students reached this semester',
    body: 'Our after-school tutoring program hit a new milestone: 1,200 students served this semester, with 89% improving at least one letter grade in math or reading.',
    publishedAt: daysAgo(1.5),
  },
  {
    id: 'news-002',
    organizationId: 'org-003',
    orgName: 'Rewild the Coast',
    title: 'Volunteers replant 5 acres of dune grass',
    body: 'Over 140 community scientists joined our weekend restoration sprint, replanting 5 acres of native dune grass along the north shoreline.',
    publishedAt: daysAgo(3),
  },
  {
    id: 'news-003',
    organizationId: 'org-006',
    orgName: 'Tables of Plenty',
    title: 'New produce route serves 400 more seniors',
    body: 'Thanks to donor support, our fresh produce delivery program added a fourth route this month, reaching 400 additional seniors every week.',
    publishedAt: daysAgo(5),
  },
  {
    id: 'news-004',
    organizationId: 'org-002',
    orgName: 'Community Health Forward',
    title: 'Mobile clinic adds Saturday hours',
    body: 'Our rural mobile clinic now runs Saturday hours in two counties, adding roughly 120 appointment slots per month for patients who work weekdays.',
    publishedAt: daysAgo(7),
  },
  {
    id: 'news-005',
    organizationId: 'org-005',
    orgName: 'Rooted Home Initiative',
    title: '38 families moved into stable housing last month',
    body: 'Our transitional housing team placed 38 families last month and prevented 21 evictions through emergency rental assistance.',
    publishedAt: daysAgo(9),
  },
  {
    id: 'news-006',
    organizationId: 'org-004',
    orgName: 'Mosaic Arts Collective',
    title: 'Community mural #66 unveiled downtown',
    body: 'The 66th neighborhood mural in our community project was unveiled this weekend, painted with 200+ local volunteers and students.',
    publishedAt: daysAgo(12),
  },
  {
    id: 'news-007',
    organizationId: 'org-007',
    orgName: 'NextGen Leaders',
    title: 'Summer internship cohort doubles',
    body: 'This summer we placed twice as many teens in paid internships as last year — 84 placements across 31 partner employers.',
    publishedAt: daysAgo(15),
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns a shallow copy of the organizations array so handlers can apply
 * in-memory filters without mutating the seed.
 */
export function getOrganizations(): Organization[] {
  return [...DEMO_ORGANIZATIONS];
}

/**
 * Returns a shallow copy of the DEMO DONOR's donations (donor-scoped view
 * backing GET /api/donations) so handlers can apply in-memory filters
 * without mutating the seed.
 */
const PAY_METHODS = ['card', 'card', 'bank', 'apple_pay', 'google_pay'] as const;
export function getDonations(): Donation[] {
  // Ensure every donation carries a payment method (rotated deterministically
  // for the donor's own list, which is authored without the field).
  return DEMO_DONATIONS.map((d, i) => ({
    ...d,
    paymentMethod: d.paymentMethod ?? PAY_METHODS[i % PAY_METHODS.length],
  }));
}

/** Returns a shallow copy of EVERY donation on the platform (all orgs). */
export function getAllDonations(): Donation[] {
  return [...DEMO_ALL_DONATIONS];
}

/**
 * Looks up a single organization by ID. Returns undefined when not found.
 */
export function getOrganizationById(id: string): Organization | undefined {
  return DEMO_ORGANIZATIONS.find((org) => org.id === id);
}
